import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import Spinner from '../common/Spinner';

interface Usuario {
  id: string;
  nome: string;
  email: string;
  tipo: string;
  eh_lider: boolean;
  avatar?: string;
}

interface ModalMembrosCanalProps {
  isOpen: boolean;
  onClose: () => void;
  canalNome: string;
  departamentoId: string | null;
}

const ModalMembrosCanal: React.FC<ModalMembrosCanalProps> = ({
  isOpen,
  onClose,
  canalNome,
  departamentoId
}) => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (isOpen && departamentoId) {
      carregarUsuarios();
    }
  }, [isOpen, departamentoId]);

  const carregarUsuarios = async () => {
    if (!departamentoId) {
      return;
    }
    
    setCarregando(true);
    try {
      const response = await api.get(`/departamentos/${departamentoId}/usuarios`);
      setUsuarios(response.data.usuarios || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setUsuarios([]);
    } finally {
      setCarregando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Membros do Canal</h2>
            <p className="text-sm text-gray-600 mt-1">{canalNome}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {carregando ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size={32} />
            </div>
          ) : usuarios.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="font-medium">Nenhum membro encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {usuarios.map((usuario) => (
                <div
                  key={usuario.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <img
                    src={usuario.avatar || '/profilepicture.png'}
                    alt={usuario.nome}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-800 truncate">{usuario.nome}</p>
                      {usuario.eh_lider && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                          Líder
                        </span>
                      )}
                      {usuario.tipo === 'admin' && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                          Admin
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{usuario.email}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {usuarios.length} {usuarios.length === 1 ? 'membro' : 'membros'}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalMembrosCanal;
